import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Package } from "lucide-react";
import { useImport } from "@/contexts/ImportContext";

interface DiscoveredProduct {
  url: string;
  name: string;
  price: number | null;
  image_url: string | null;
}

interface BulkImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  siteName: string;
  products: DiscoveredProduct[];
  onImportComplete: () => void;
}

export function BulkImportDialog({ 
  open, 
  onOpenChange, 
  workspaceId, 
  siteName, 
  products,
  onImportComplete 
}: BulkImportDialogProps) {
  const { startImport } = useImport();
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());

  const toggleProduct = (url: string) => {
    const newSelected = new Set(selectedUrls);
    if (newSelected.has(url)) {
      newSelected.delete(url);
    } else {
      newSelected.add(url);
    }
    setSelectedUrls(newSelected);
  };

  const toggleAll = () => {
    if (selectedUrls.size === products.length) {
      setSelectedUrls(new Set());
    } else {
      setSelectedUrls(new Set(products.map(p => p.url)));
    }
  };

  const handleStartImport = () => {
    if (selectedUrls.size === 0) return;

    const selectedProducts = products.filter(p => selectedUrls.has(p.url));
    startImport(selectedProducts, workspaceId, siteName, onImportComplete);
    
    setSelectedUrls(new Set());
    onOpenChange(false);
  };

  const handleClose = () => {
    setSelectedUrls(new Set());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Importar Produtos de {siteName}
          </DialogTitle>
          <DialogDescription>
            Encontramos {products.length} produto(s). Selecione quais deseja importar.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between py-2 border-b">
          <span className="text-sm text-muted-foreground">
            {selectedUrls.size} de {products.length} selecionado(s)
          </span>
          <Button variant="outline" size="sm" onClick={toggleAll}>
            {selectedUrls.size === products.length ? "Desmarcar Todos" : "Selecionar Todos"}
          </Button>
        </div>

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-2">
            {products.map((product) => (
              <div 
                key={product.url}
                className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                onClick={() => toggleProduct(product.url)}
              >
                <Checkbox 
                  checked={selectedUrls.has(product.url)}
                  onCheckedChange={() => toggleProduct(product.url)}
                />
                {product.image_url && (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-12 h-12 object-cover rounded"
                    onError={(e) => e.currentTarget.style.display = 'none'}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{product.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{product.url}</p>
                </div>
                {product.price && (
                  <Badge variant="secondary">
                    R$ {product.price.toFixed(2)}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleStartImport} 
            disabled={selectedUrls.size === 0}
          >
            Importar {selectedUrls.size} Produto(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
